import type { CSSProperties, FC, AnimationEventHandler } from 'react'
import cx from 'classnames'
import type { Photo, Video } from '../../types';
import { UnstyledButton } from '../UnstyledButton';
import { useI18n } from '../../i18n';

import styles from './PhotoWidget.module.css'

type Props = {
  items: Array<Photo | Video>;
  onClick: (gallery: Array<Photo | Video>) => void;
  className?: string;
  style?: CSSProperties;
  onAnimationStart?: AnimationEventHandler<HTMLButtonElement>;
  onAnimationEnd?: AnimationEventHandler<HTMLButtonElement>;
};

export const PhotoWidget: FC<Props> = ({
  items,
  onClick,
  className,
  style,
  onAnimationStart,
  onAnimationEnd,
}) => {
  const i18n = useI18n();
  const modifier =
    items.length === 1
      ? styles.one
      : items.length === 2
      ? styles.two
      : styles.many;

  const resolvePreviewSrc = (item: Photo | Video) => {
    if (item.type === 'video') {
      return item.thumbnailSrc ?? item.src
    }
    return item.thumbnailSrc ?? item.src
  }

  return (
    <UnstyledButton
      className={cx(styles.root, modifier, className)}
      style={style}
      onAnimationStart={onAnimationStart}
      onAnimationEnd={onAnimationEnd}
      onClick={() => onClick(items)}
      aria-label={i18n.chatInput.openMediaGallery}
    >
      <div className={styles.wrapper}>
        {items.map((item, i) =>
          i <= 2 ? (
            <div className={styles.item} key={item.id}>
              <img
                className={styles.img}
                src={resolvePreviewSrc(item)}
                width={item.type === 'photo' ? item.width : undefined}
                height={item.type === 'photo' ? item.height : undefined}
                alt={item.caption}
              />
              {item.caption && (
                <span className={styles.caption}>{item.caption}</span>
              )}
            </div>
          ) : null
        )}
        {items.length > 3 && (
          <div className={styles.counter}>+{items.length - 3}</div>
        )}
      </div>
    </UnstyledButton>
  );
};
